import {
  USER_TYPE_STUDENT,
  USER_TYPE_TEACHER,
} from '@/const/userType'

/**
 * Roles that get `body.campus-ui` / Vuex `isCampusUi` (shell + product pages).
 * Keep to student/teacher only — district/school admin layouts are not designed for
 * `_shell.scss`. Settings hub Campus chrome uses `isSettingsCampusUiEnabled` instead
 * and works for district/school without this list.
 */
export const CAMPUS_UI_USER_TYPES = [
  USER_TYPE_STUDENT,
  USER_TYPE_TEACHER,
]

/** Stored meta theme ids (extend this list when adding themes). */
export const CAMPUS_UI_THEME_CLASSIC = 'classic'
export const CAMPUS_UI_THEME_CAMPUS = 'campus'
export const CAMPUS_UI_THEME_DEFAULT = CAMPUS_UI_THEME_CLASSIC

/**
 * Selectable themes for Settings → Themes.
 * Add new entries here; keep `legacyValues` for older meta (`0` / `1`).
 */
export const CAMPUS_UI_THEMES = [
  {
    id: CAMPUS_UI_THEME_CLASSIC,
    labelKey: 'setting-module.campus-ui-style-old',
    hintKey: 'setting-module.campus-ui-hint-old',
    legacyValues: ['0'],
    isCampusChrome: false,
  },
  {
    id: CAMPUS_UI_THEME_CAMPUS,
    labelKey: 'setting-module.campus-ui-style-new',
    hintKey: 'setting-module.campus-ui-hint-new',
    legacyValues: ['1'],
    isCampusChrome: true,
  },
]

export function getCampusUiThemeById(themeId) {
  return CAMPUS_UI_THEMES.find(theme => theme.id === themeId) || null
}

/**
 * Normalize any stored meta / form value to a theme id.
 * Default is Classic (old UI).
 */
export function normalizeCampusUiTheme(value) {
  const raw = value === undefined || value === null ? '' : String(value).trim()
  if (!raw) return CAMPUS_UI_THEME_DEFAULT

  const direct = getCampusUiThemeById(raw)
  if (direct) return direct.id

  const fromLegacy = CAMPUS_UI_THEMES.find(theme => theme.legacyValues.includes(raw))
  if (fromLegacy) return fromLegacy.id

  // Unknown future/invalid values fall back to Classic
  return CAMPUS_UI_THEME_DEFAULT
}

export function isCampusChromeTheme(value) {
  const theme = getCampusUiThemeById(normalizeCampusUiTheme(value))
  return Boolean(theme?.isCampusChrome)
}

/**
 * Whether Settings hub panels should use the Campus UI chrome.
 * District managers use their own campus_ui; schools prefer parent district value.
 */
export function getEffectiveCampusUiValue(optionsLocal = {}) {
  if (optionsLocal?.district && Object.prototype.hasOwnProperty.call(optionsLocal.district, 'campus_ui')) {
    return optionsLocal.district.campus_ui
  }
  return optionsLocal?.campus_ui
}

export function isSettingsCampusUiEnabled(optionsLocal = {}) {
  return isCampusChromeTheme(getEffectiveCampusUiValue(optionsLocal))
}

export function isCampusUiUserType(usertype) {
  return CAMPUS_UI_USER_TYPES.includes(usertype)
}

export function isCampusUiEnabledFromMeta(meta) {
  if (!Array.isArray(meta)) return false
  const row = meta.find(item => item.key === 'campus_ui')
  return isCampusChromeTheme(row?.value)
}

/**
 * Patch campus_ui into a districtInfo-like object (immutable-style return).
 */
export function withCampusUiMetaValue(districtInfo, value) {
  const themeId = normalizeCampusUiTheme(value)
  const base = districtInfo && typeof districtInfo === 'object' ? { ...districtInfo } : {}
  const meta = Array.isArray(base.user_meta) ? [...base.user_meta] : []
  const index = meta.findIndex(item => item.key === 'campus_ui')
  const entry = { key: 'campus_ui', value: themeId }
  if (index >= 0) {
    meta[index] = { ...meta[index], ...entry }
  } else {
    meta.push(entry)
  }
  base.user_meta = meta
  return base
}

/** Options for b-form-select (pass $t for labels). */
export function getCampusUiThemeSelectOptions(translate = key => key) {
  return CAMPUS_UI_THEMES.map(theme => ({
    value: theme.id,
    text: translate(theme.labelKey),
  }))
}

export function getCampusUiThemeHintKey(themeId) {
  const theme = getCampusUiThemeById(normalizeCampusUiTheme(themeId))
  return theme?.hintKey || 'setting-module.campus-ui-hint-old'
}
